import { useFormik } from 'formik'
import React, { useEffect } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'

import { ManageReferralsSchema } from '../../../validation/validate'

import { NumericFormat } from 'react-number-format'
import { toast } from 'react-toastify'
import { PRIMARY_COLOR } from '../../../constants'
import { getCurrencySymbol, parseCurrencyToNumber } from '../../../helpers'
import {
  useGetReferralConfigQuery,
  useGetReferralStatusQuery,
  useToggleReferralMutation,
  useUpdateReferralConfigMutation,
} from '../../../redux-toolkits/loyalty/loyalty.slice'
import { Checkbox } from '../../inputs/Checkbox'
import { TextInput } from '../../inputs/textInput'
import { LoadingOval } from '../../spinner/Spinner'
import CustSwitch from '../../switch'

type Config = {
  enableReferrals: boolean
  type: 'percentage' | 'amount'
  value: string
}

export const ManageReferralsModal: React.FC<{
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
}) => {
  const {
    data: referralStatus,
    isFetching: fetchingReferralStatus,
    refetch: refetchReferralStatus,
  } = useGetReferralStatusQuery(undefined, {
    skip: !open,
  })

  const {
    data: referralConfig,
    isFetching: fetchingReferralConfig,
    refetch: refetchReferralConfig,
  } = useGetReferralConfigQuery(undefined, {
    skip: !open,
  })

  const [toggleReferral, { isLoading: togglingReferral }] =
    useToggleReferralMutation()

  const [updateReferralConfig, { isLoading: updatingReferralConfig }] =
    useUpdateReferralConfigMutation()

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    isValid,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      enableReferrals: referralStatus?.data?.referralReward ?? false,
      type:
        referralConfig?.data?.fee?.feeCharge === 'Percentage'
          ? 'percentage'
          : 'amount',
      value:
        (referralConfig?.data?.fee?.feeCharge === 'Percentage'
          ? referralConfig?.data?.fee?.feePercentage
          : referralConfig?.data?.fee?.feeAmount) ?? '',
    } as Config,
    enableReinitialize: true,
    validationSchema: ManageReferralsSchema,
    onSubmit: async (values, { resetForm }) => {
      const toggleResp = await toggleReferral({
        referralReward: values.enableReferrals,
      })

      if (toggleResp?.data?.success) {
        const updateResp = await updateReferralConfig({
          feeCharge: values.type === 'amount' ? 'Flat' : 'Percentage',
          feePercentage:
            values.type === 'percentage'
              ? parseCurrencyToNumber(values.value)
              : undefined,
          feeAmount:
            values.type === 'amount'
              ? parseCurrencyToNumber(values.value)
              : undefined,
        })

        if (updateResp?.data?.success) {
          resetForm()
          onSuccessCallback()
          toast.success('Operation successful  ')
        }
      }
    },
  })

  useEffect(() => {
    if (open) {
      refetchReferralConfig()
      refetchReferralStatus()
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={'Manage referral rewards'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="min-h-[200px] flex flex-col w-full items-center justify-center">
        {fetchingReferralStatus || fetchingReferralConfig ? (
          <LoadingOval color={PRIMARY_COLOR} />
        ) : (
          <>
            <div className="flex flex-row gap-6 items-center py-4 border-b">
              <p className="flex flex-col gap-0.5">
                <span className="text-black text-base font-medium">
                  Referral rewards
                </span>
                <span className="text-sec-black text-sm base font-normal">
                  Give referrers the ability to earn rewards when their referees
                  make a purchase.
                </span>
              </p>

              <div className="min-w-fit">
                <CustSwitch
                  checked={values.enableReferrals}
                  disabled={false}
                  onChange={() => {
                    setFieldValue('enableReferrals', !values.enableReferrals)
                  }}
                />
              </div>
            </div>

            {values.enableReferrals ? (
              <>
                <p className="w-full text-black text-base font-medium py-4">
                  Select a reward configuration
                </p>

                <div className="py-4 border-b flex flex-col gap-2">
                  <Checkbox
                    checkStatus={values.type === 'amount'}
                    text="Referrers earn a set cash reward for every referee order"
                    handleChange={(checked) => {
                      if (checked) {
                        setFieldValue('type', 'amount')
                        setFieldValue('value', '')
                      }
                    }}
                    disabled={false}
                    textPosition="left"
                  />

                  {values.type === 'amount' ? (
                    <NumericFormat
                      suffix={undefined}
                      prefix={getCurrencySymbol('NGN')}
                      decimalScale={2}
                      thousandSeparator
                      customInput={TextInput}
                      type="text"
                      value={values.value}
                      name="value"
                      placeholder="Amount (₦)"
                      hasIcon={false}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={
                        touched.value && errors.value ? errors.value : undefined
                      }
                    />
                  ) : undefined}
                </div>

                <div className="py-4 border-b flex flex-col gap-2">
                  <Checkbox
                    checkStatus={values.type === 'percentage'}
                    text="Referrers earn a percentage of the total amount of each referee order"
                    handleChange={(checked) => {
                      if (checked) {
                        setFieldValue('type', 'percentage')
                        setFieldValue('value', '')
                      }
                    }}
                    disabled={false}
                    textPosition="left"
                  />

                  {values.type === 'percentage' ? (
                    <NumericFormat
                      suffix={'%'}
                      prefix={undefined}
                      decimalScale={2}
                      thousandSeparator
                      customInput={TextInput}
                      type="text"
                      value={values.value}
                      name="value"
                      placeholder="Percentage (%)"
                      hasIcon={false}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={
                        touched.value && errors.value ? errors.value : undefined
                      }
                    />
                  ) : undefined}
                </div>
              </>
            ) : undefined}

            <Button
              label="Save"
              type="button"
              className="mt-6 w-full"
              disabled={!isValid || updatingReferralConfig || togglingReferral}
              loading={updatingReferralConfig || togglingReferral}
              primary
              onClick={() => {
                handleSubmit()
              }}
            />
          </>
        )}
      </div>
    </ModalComponent>
  )
}
